/**
 * Score, victory and the game's history (ADR-0061).
 *
 * Score is public — like military strength, it is the kind of thing envoys report — and
 * shown all game, so a player always knows where they stand. Victory is checked after
 * anything that can end a civilisation and as each turn begins; once won, the result is
 * recorded and play may go on. Which victories count is data (`rules.victory`): only
 * conquest for now, and the checks are a table so others can be added beside it.
 */

import type { Content } from '../../content/schema/index.js';
import type { GameEvent } from './commands/types.js';
import { wondersBuilt } from './cities/wonders.js';
import { tileCount } from './state/grid.js';
import type { GameState, GameResult, HistorySeries, Id } from './state/types.js';

export interface ScorePart {
  label: string;
  /** What was counted: citizens, tiles, technologies… */
  count: number;
  points: number;
}

export interface Score {
  total: number;
  parts: ScorePart[];
}

/** The raw measures score is made of, also what the history samples. */
export type Measures = { [K in Exclude<keyof HistorySeries, 'score'>]: number };

export function measures(state: GameState, civId: Id): Measures {
  let population = 0;
  let culture = 0;
  let cities = 0;
  for (const city of Object.values(state.cities)) {
    if (city.owner !== civId) continue;
    population += city.population;
    culture += city.culture;
    cities++;
  }
  let land = 0;
  const owner = state.map.owner;
  for (let i = 0; i < tileCount(state.map); i++) if (owner[i] === civId) land++;
  return { land, techs: state.civs[civId]?.techs.length ?? 0, population, culture, cities };
}

export function scoreOf(state: GameState, content: Content, civId: Id): Score {
  const rules = content.rules.score;
  const m = measures(state, civId);
  const wonders = wondersBuilt(state, content).filter((w) => w.city.owner === civId).length;
  const parts: ScorePart[] = [
    { label: 'citizens', count: m.population, points: m.population * rules.perCitizen },
    { label: 'land', count: m.land, points: m.land * rules.perTile },
    { label: 'technologies', count: m.techs, points: m.techs * rules.perTech },
    { label: 'wonders', count: wonders, points: wonders * rules.perWonder },
    { label: 'culture', count: m.culture, points: m.culture * rules.perCulture },
  ];
  const result = state.result;
  if (result && result.winner === civId) {
    const early = Math.max(0, rules.earlyWinReference - result.turn) * rules.earlyWinPerTurn;
    parts.push({ label: `victory, turn ${result.turn}`, count: 1, points: rules.victory + early });
  }
  for (const part of parts) part.points = Math.round(part.points);
  return { total: parts.reduce((sum, p) => sum + p.points, 0), parts };
}

type Check = (state: GameState) => GameResult | null;

/** Each victory the engine knows, by the name `rules.victory.conditions` uses. */
const CHECKS: Record<Content['rules']['victory']['conditions'][number], Check> = {
  // The last civilisation standing.
  conquest: (state) => {
    const alive = Object.values(state.civs).filter((c) => !c.defeated);
    if (alive.length !== 1 || Object.keys(state.civs).length < 2) return null;
    return { winner: alive[0]!.id, kind: 'conquest', turn: state.meta.turn };
  },
};

/** Record a victory the moment one happens. Once there is a result, nothing changes it. */
export function checkVictory(state: GameState, content: Content): GameEvent[] {
  if (state.result) return [];
  for (const name of content.rules.victory.conditions) {
    const result = CHECKS[name](state);
    if (result) {
      state.result = result;
      recordHistory(state, content, true);
      return [{ t: 'GameWon', winner: result.winner, kind: result.kind, turn: result.turn }];
    }
  }
  return [];
}

/** Sample every civilisation's measures, every `historyEvery` turns (or now, if `force`). */
export function recordHistory(state: GameState, content: Content, force = false): void {
  const turn = state.meta.turn;
  const history = state.history;
  if (history.turns[history.turns.length - 1] === turn) return;
  if (!force && turn % content.rules.score.historyEvery !== 0) return;
  const samples = history.turns.length;
  history.turns.push(turn);
  for (const civ of Object.values(state.civs).sort((a, b) => a.id - b.id)) {
    let series = history.civs[civ.id];
    if (!series) {
      // A civilisation first sampled late is given zeros for what came before, so every
      // series stays in step with the turns.
      const zeros = (): number[] => new Array<number>(samples).fill(0);
      series = { score: zeros(), land: zeros(), techs: zeros(), population: zeros(), culture: zeros(), cities: zeros() };
      history.civs[civ.id] = series;
    }
    const m = measures(state, civ.id);
    series.score.push(scoreOf(state, content, civ.id).total);
    series.land.push(m.land);
    series.techs.push(m.techs);
    series.population.push(m.population);
    series.culture.push(m.culture);
    series.cities.push(m.cities);
  }
}
